<template>
    <section class="taxonomy-explorer" :aria-label="`Clasificación taxonómica completa de ${productName}`">
        <div class="taxonomy-toolbar" aria-label="Controles del mapa">
            <div class="taxonomy-zoom-controls">
                <button type="button" aria-label="Alejar mapa" @click="zoomBy(-0.1)">−</button>
                <strong aria-live="polite">{{ Math.round(view.scale * 100) }}%</strong>
                <button type="button" aria-label="Acercar mapa" @click="zoomBy(0.1)">+</button>
                <button type="button" class="taxonomy-reset" @click="resetView">Centrar</button>
                <button type="button" class="taxonomy-reset" @click="toggleAll">
                    {{ expandedDepth === maxDepth ? 'Contraer' : 'Mostrar todo' }}
                </button>
            </div>
            <a :href="spreadsheetUrl" target="_blank" rel="noopener noreferrer">
                Ver fuente taxonómica
            </a>
        </div>

        <div class="taxonomy-canvas-wrap">
            <svg
                ref="canvas"
                class="taxonomy-canvas"
                viewBox="0 0 620 1000"
                role="img"
                :aria-label="`Mapa vertical de la clasificación taxonómica de ${productName}`"
                @wheel.prevent="onWheel"
                @dblclick.prevent="zoomAtPointer"
                @pointerdown="startPan"
                @pointermove="movePan"
                @pointerup="endPan"
                @pointercancel="endPan"
                @pointerleave="endPan"
            >
                <defs>
                    <filter id="coffee-taxonomy-shadow" x="-30%" y="-30%" width="160%" height="160%">
                        <feDropShadow dx="0" dy="5" stdDeviation="6" flood-color="#173f27" flood-opacity="0.14" />
                    </filter>
                    <linearGradient id="coffee-taxonomy-background" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0" stop-color="#fffdf2" />
                        <stop offset="1" stop-color="#f5f7e9" />
                    </linearGradient>
                    <pattern id="coffee-taxonomy-grid" width="36" height="36" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="1.2" fill="#2b7447" opacity="0.13" />
                    </pattern>
                </defs>

                <rect width="620" height="1000" rx="22" fill="url(#coffee-taxonomy-background)" />
                <rect width="620" height="1000" rx="22" fill="url(#coffee-taxonomy-grid)" />

                <g :transform="`translate(${view.x} ${view.y}) scale(${view.scale})`">
                    <path v-for="edge in visibleEdges" :key="edge.id" :d="edge.path" class="taxonomy-edge" />

                    <g
                        v-for="node in visibleNodes"
                        :key="node.id"
                        class="taxonomy-node"
                        :transform="`translate(${node.x} ${node.y})`"
                        role="button"
                        tabindex="0"
                        :aria-label="`${node.rank}: ${node.name}`"
                        @click.stop="openTerm(node)"
                        @keydown.enter.prevent="openTerm(node)"
                        @keydown.space.prevent="openTerm(node)"
                    >
                        <rect
                            :width="nodeWidth"
                            :height="nodeHeight"
                            rx="18"
                            :fill="node.color"
                            filter="url(#coffee-taxonomy-shadow)"
                        />
                        <text class="taxonomy-node-rank" x="18" y="27">{{ node.rank }}</text>
                        <text class="taxonomy-node-name" x="18" y="59">{{ node.name }}</text>
                    </g>
                </g>
            </svg>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'

const props = defineProps({
    terms: { type: Array, required: true },
    productName: { type: String, default: 'este producto' },
    spreadsheetUrl: {
        type: String,
        default: 'https://docs.google.com/spreadsheets/d/1-QPtvCS1rGYwBOwP-VewgDGsKS-N-pDC3Nvt9jRFTaU/edit?gid=0#gid=0'
    }
})

const nodeWidth = 250
const nodeHeight = 78

const rankColors = [
    '#efe5ff', '#dcecff', '#d9f5f2', '#d9f5e2', '#d9f3d0', '#e4f4c8', '#ecf5c9', '#f1f3c6',
    '#f5f1c7', '#f8edbd', '#fbe8b6', '#fff0b8', '#ffe2a8', '#ffd5b2', '#ffceb0', '#ffc9b0',
    '#ffc8b3', '#ffc4b1', '#ffc1af', '#ffbdb8', '#f7b8cb'
]

function buildNodes(terms) {
    const termsBySlug = new Map(terms.map(term => [term.slug, term]))
    const depthCache = new Map()
    const getDepth = term => {
        if (depthCache.has(term.slug)) return depthCache.get(term.slug)
        const depth = term.parent && termsBySlug.has(term.parent)
            ? getDepth(termsBySlug.get(term.parent)) + 1
            : 0
        depthCache.set(term.slug, depth)
        return depth
    }

    return terms.map(term => {
        const depth = getDepth(term)
        const siblings = terms.filter(candidate => candidate.parent === term.parent && getDepth(candidate) === depth)
        const siblingIndex = siblings.findIndex(candidate => candidate.slug === term.slug)
        const x = siblings.length > 1 ? 35 + siblingIndex * 300 : 185

        return {
            id: term.slug,
            parentId: term.parent,
            depth,
            rank: term.rank,
            name: term.name,
            url: term.url,
            x,
            y: 28 + depth * 108,
            color: rankColors[depth % rankColors.length]
        }
    })
}

const nodes = buildNodes(props.terms)
const maxDepth = Math.max(...nodes.map(node => node.depth))
const canvas = ref(null)
const expandedDepth = ref(maxDepth)
const view = reactive({ x: 0, y: 24, scale: 0.55 })
const drag = reactive({ active: false, moved: false, pointerId: null, x: 0, y: 0 })
const pointers = new Map()
const pinch = reactive({ active: false, distance: 0, scale: 1, worldX: 0, worldY: 0 })

const visibleNodes = computed(() => nodes.filter(node => node.depth <= expandedDepth.value))
const visibleNodeIds = computed(() => new Set(visibleNodes.value.map(node => node.id)))
const visibleEdges = computed(() => visibleNodes.value
    .filter(node => node.parentId && visibleNodeIds.value.has(node.parentId))
    .map(node => {
        const parent = nodes.find(candidate => candidate.id === node.parentId)
        const startX = parent.x + nodeWidth / 2
        const startY = parent.y + nodeHeight
        const endX = node.x + nodeWidth / 2
        const endY = node.y
        const bend = Math.max(30, (endY - startY) / 2)
        return {
            id: `${parent.id}-${node.id}`,
            path: `M ${startX} ${startY} C ${startX} ${startY + bend}, ${endX} ${endY - bend}, ${endX} ${endY}`
        }
    }))

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

function zoomBy(amount, origin = { x: 310, y: 500 }) {
    const oldScale = view.scale
    const newScale = clamp(oldScale + amount, 0.38, 1.45)
    const worldX = (origin.x - view.x) / oldScale
    const worldY = (origin.y - view.y) / oldScale
    view.x = origin.x - worldX * newScale
    view.y = origin.y - worldY * newScale
    view.scale = newScale
}

function onWheel(event) {
    const rect = canvas.value.getBoundingClientRect()
    zoomBy(event.deltaY < 0 ? 0.08 : -0.08, {
        x: (event.clientX - rect.left) * (620 / rect.width),
        y: (event.clientY - rect.top) * (1000 / rect.height)
    })
}

function zoomAtPointer(event) {
    const rect = canvas.value.getBoundingClientRect()
    zoomBy(0.18, {
        x: (event.clientX - rect.left) * (620 / rect.width),
        y: (event.clientY - rect.top) * (1000 / rect.height)
    })
}

function pointerMidpoint(activePointers) {
    const rect = canvas.value.getBoundingClientRect()
    return {
        x: ((activePointers[0].x + activePointers[1].x) / 2 - rect.left) * (620 / rect.width),
        y: ((activePointers[0].y + activePointers[1].y) / 2 - rect.top) * (1000 / rect.height)
    }
}

function pointerDistance(activePointers) {
    return Math.hypot(
        activePointers[0].x - activePointers[1].x,
        activePointers[0].y - activePointers[1].y
    )
}

function startPan(event) {
    if (event.button !== undefined && event.button !== 0) return
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    canvas.value.setPointerCapture?.(event.pointerId)

    if (pointers.size === 2) {
        const activePointers = Array.from(pointers.values())
        const midpoint = pointerMidpoint(activePointers)
        pinch.active = true
        pinch.distance = Math.max(pointerDistance(activePointers), 1)
        pinch.scale = view.scale
        pinch.worldX = (midpoint.x - view.x) / view.scale
        pinch.worldY = (midpoint.y - view.y) / view.scale
        drag.active = false
        drag.moved = true
        return
    }

    drag.active = true
    drag.moved = false
    drag.pointerId = event.pointerId
    drag.x = event.clientX
    drag.y = event.clientY
}

function movePan(event) {
    if (!pointers.has(event.pointerId)) return
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (pinch.active && pointers.size >= 2) {
        const activePointers = Array.from(pointers.values()).slice(0, 2)
        const midpoint = pointerMidpoint(activePointers)
        const newScale = clamp(
            pinch.scale * (pointerDistance(activePointers) / pinch.distance),
            0.38,
            1.45
        )
        view.x = midpoint.x - pinch.worldX * newScale
        view.y = midpoint.y - pinch.worldY * newScale
        view.scale = newScale
        drag.moved = true
        return
    }

    if (!drag.active || event.pointerId !== drag.pointerId) return
    const rect = canvas.value.getBoundingClientRect()
    const dx = (event.clientX - drag.x) * (620 / rect.width)
    const dy = (event.clientY - drag.y) * (1000 / rect.height)
    if (Math.abs(dx) + Math.abs(dy) > 1) drag.moved = true
    view.x += dx
    view.y += dy
    drag.x = event.clientX
    drag.y = event.clientY
}

function endPan(event) {
    pointers.delete(event.pointerId)
    canvas.value?.releasePointerCapture?.(event.pointerId)
    pinch.active = false
    drag.active = false
    drag.pointerId = null
}

function openTerm(node) {
    if (drag.moved) {
        drag.moved = false
        return
    }
    Inertia.visit(node.url)
}

function resetView() {
    view.x = 0
    view.y = 24
    view.scale = window.innerWidth <= 640 ? 0.72 : 0.55
}

function toggleAll() {
    expandedDepth.value = expandedDepth.value === maxDepth ? 4 : maxDepth
    resetView()
}

onMounted(resetView)
</script>

<style scoped>
.taxonomy-explorer { margin-top: 1.25rem; overflow: hidden; background: #fffdf3; border: 1px solid rgba(22, 75, 45, 0.16); border-radius: 1rem; box-shadow: 0 12px 28px rgba(22, 75, 45, 0.08); }
.taxonomy-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 0.65rem; padding: 0.55rem 0.75rem; background: #f7f8ef; border-bottom: 1px solid rgba(22, 75, 45, 0.1); }
.taxonomy-zoom-controls { display: flex; align-items: center; gap: 0.32rem; }
.taxonomy-toolbar button { min-width: 1.9rem; min-height: 1.9rem; padding: 0 0.5rem; color: white; font-weight: 800; background: #176438; border: 0; border-radius: 0.6rem; cursor: pointer; }
.taxonomy-toolbar strong { min-width: 2.6rem; color: #164b2d; font-size: 0.68rem; text-align: center; }
.taxonomy-toolbar .taxonomy-reset { font-size: 0.66rem; }
.taxonomy-toolbar a { color: #176438; font-size: 0.69rem; font-weight: 800; text-decoration: underline; text-underline-offset: 2px; }
.taxonomy-canvas-wrap { padding: 0; background: transparent; }
.taxonomy-canvas { display: block; width: 100%; height: auto; aspect-ratio: 620 / 1000; overflow: hidden; background: #fffdf2; border-radius: 0.9rem; cursor: grab; touch-action: none; user-select: none; }
.taxonomy-canvas:active { cursor: grabbing; }
.taxonomy-edge { fill: none; stroke: #4c8b63; stroke-width: 6; stroke-linecap: round; opacity: 0.65; }
.taxonomy-node { cursor: pointer; outline: none; }
.taxonomy-node rect { stroke: rgba(22, 75, 45, 0.24); stroke-width: 2; transition: stroke 0.2s ease, stroke-width 0.2s ease; }
.taxonomy-node:hover rect, .taxonomy-node:focus rect { stroke: #176438; stroke-width: 5; }
.taxonomy-node-rank { fill: #547160; font-size: 11px; font-weight: 800; letter-spacing: 1.25px; }
.taxonomy-node-name { fill: #123f27; font-size: 19px; font-weight: 800; }

@media (max-width: 640px) {
    .taxonomy-toolbar { align-items: stretch; flex-direction: column; }
    .taxonomy-zoom-controls { width: 100%; }
    .taxonomy-toolbar .taxonomy-reset { flex: 1; }
    .taxonomy-toolbar a { padding: 0.3rem 0.2rem; text-align: center; }
}
</style>
